import { memo, useCallback } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import { cn } from '@/lib/cn';

// ── Branch key pill ────────────────────────────────────────────────────────────

function BranchKeyPill({ branchKey }: { branchKey: string }) {
  const isSpecial = ['timeout', 'invalid', 'fallback', 'done', 'failed'].includes(branchKey);
  const isDigit = /^[0-9*#]$/.test(branchKey);
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none shadow-sm',
        isSpecial
          ? 'bg-amber-50 border-amber-300 text-amber-700'
          : isDigit
          ? 'bg-slate-50 border-slate-300 text-slate-700'
          : 'bg-white border-slate-200 text-slate-600',
      )}
    >
      {branchKey}
    </span>
  );
}

// ── Edge data shape ────────────────────────────────────────────────────────────

export interface IvrCanvasEdgeData extends Record<string, unknown> {
  branchKey: string;
  edgeId: string;
  onEdgeClick: (edgeId: string) => void;
}

// ── Main edge component ────────────────────────────────────────────────────────

export const IvrCanvasEdge = memo(function IvrCanvasEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  markerEnd,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleLabelClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (data?.onEdgeClick) {
        (data.onEdgeClick as (id: string) => void)(data.edgeId as string ?? id);
      }
    },
    [data, id],
  );

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          strokeWidth: selected ? 2.5 : 1.5,
          stroke: selected ? '#6366f1' : '#cbd5e1',
          strokeDasharray: undefined,
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
          onClick={handleLabelClick}
        >
          <div
            className={cn(
              'cursor-pointer transition-all duration-100 hover:scale-105',
              selected && 'ring-1 ring-indigo-300 rounded',
            )}
          >
            <BranchKeyPill branchKey={(data?.branchKey as string) ?? '?'} />
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
});
